import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Undo2 } from 'lucide-react';
import { fetchHCLData } from '../../utils/hclBlob';
import { evaluateFilterExpression, parseLooseNumber, type FilterCell } from '../../utils/columnFilter';
import { FilterPresets } from '../ui/filter-presets';
import { cleanFilters, type FilterSettings } from '../../utils/filterPresets';

interface HCLData {
  Tanggal?: string;
  "Kode Emiten"?: string;
  "Nama Perusahaan"?: string;
  "HCL (%)"?: string | number;
  Status?: string;
}

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useEffect(() => {
    const timeout = setTimeout(() => { onChangeRef.current(value); }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return <input {...props} value={value} onChange={e => setValue(e.target.value)} />;
}

type SortKey = 'Tanggal' | 'Kode Emiten' | 'Nama Perusahaan' | 'HCL (%)' | 'Status';

// Header per kolom (filter & sort pakai key yang sama), juga untuk deskripsi Presets
const COLUMN_LABEL: Record<SortKey, string> = {
  Tanggal: 'Tanggal',
  'Kode Emiten': 'Ticker',
  'Nama Perusahaan': 'Nama Perusahaan',
  'HCL (%)': 'Consentrate',
  Status: 'Status',
};
const isColumn = (k: unknown): k is SortKey => typeof k === 'string' && Object.prototype.hasOwnProperty.call(COLUMN_LABEL, k);
const columnLabel = (k: string) => (isColumn(k) ? COLUMN_LABEL[k] : k);
const DEFAULT_SORT: { key: SortKey; direction: 'asc' | 'desc' } = { key: 'Tanggal', direction: 'desc' };

const formatDate = (d?: string) => {
  if (!d || d.length !== 8) return d;
  return `${d.substring(0,4)}-${d.substring(4,6)}-${d.substring(6,8)}`;
};

// Warna statis per kolom (mirror render)
const cellColorClass = (col: string): string =>
  col === 'HCL (%)' ? 'text-pink-400'
  : col === 'Tanggal' || col === 'Status' ? 'text-gray-400'
  : 'text-gray-300';

export const HCL: React.FC = () => {
  const [data, setData] = useState<HCLData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);

  // Filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  // Sorting
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(DEFAULT_SORT);

  // Presets: filter row + sort; the date range stays out (it moves every day)
  const presetCurrent = useMemo<FilterSettings>(() => ({
    filters: cleanFilters(columnFilters),
    sort: sortConfig ? { key: sortConfig.key, direction: sortConfig.direction } : null,
  }), [columnFilters, sortConfig]);

  // Defensive: an old preset may hold unknown columns or a bad sort; the data never needs a refetch
  const applyPreset = (s: FilterSettings) => {
    setColumnFilters(Object.fromEntries(Object.entries(cleanFilters(s?.filters)).filter(([k]) => isColumn(k))));
    const sort = s?.sort;
    setSortConfig(sort && isColumn(sort.key) && (sort.direction === 'asc' || sort.direction === 'desc')
      ? { key: sort.key, direction: sort.direction }
      : sort === null ? null : DEFAULT_SORT);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHCLData();
      setData(result.data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat data HCL');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Date Range Filter
      if (row.Tanggal) {
        const rowDate = formatDate(row.Tanggal);
        if (rowDate) {
          if (dateFrom && rowDate < dateFrom) return false;
          if (dateTo && rowDate > dateTo) return false;
        }
      }

      // Filter ekspresi: token warna + kondisi numerik + teks, digabung and/or
      for (const col of Object.keys(columnFilters)) {
        const expr = columnFilters[col];
        if (!expr || !expr.trim()) continue;

        const raw = (row as any)[col];
        if (raw === undefined || raw === null) return false;

        const text = col === 'Tanggal'
          ? (formatDate(raw)?.toLowerCase() || '')
          : String(raw).toLowerCase();

        const cell: FilterCell = {
          value: col === 'HCL (%)' ? parseLooseNumber(String(raw)) : NaN,
          text,
          colorClass: cellColorClass(col),
          isNumeric: col === 'HCL (%)',
        };
        if (!evaluateFilterExpression(expr, cell)) return false;
      }
      return true;
    });
  }, [data, columnFilters, dateFrom, dateTo]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aVal = (a as any)[sortConfig.key] ?? '';
      let bVal = (b as any)[sortConfig.key] ?? '';

      if (sortConfig.key === 'HCL (%)') {
        aVal = parseFloat(String(aVal).replace(',', '.')) || 0;
        bVal = parseFloat(String(bVal).replace(',', '.')) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const SortIndicator = ({ col }: { col: SortKey }) => {
    if (sortConfig?.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const thClass = (col: SortKey) =>
    `px-1 py-0 text-center whitespace-nowrap cursor-pointer select-none hover:bg-white/10 transition-colors`;

  return (
    <div className="flex flex-col h-full bg-[#0f172a] text-white overflow-hidden relative">
      {loading && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }} />
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* ── Top Control Bar ── */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-3 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-4 md:gap-x-8 md:gap-y-4">
          
          {/* Date Range — inline */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap text-white">Date Range:</label>
            <div className="relative flex items-center">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <span className="text-sm text-gray-400">to</span>
            <div className="relative flex items-center">
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto">
            <button
              onClick={fetchData}
              disabled={loading}
              className="h-8 px-4 bg-white text-black hover:bg-gray-200 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
            >
              Show
            </button>
            <button
              onClick={() => {
                setColumnFilters({});
                setSortConfig({ key: 'Tanggal', direction: 'desc' });
              }}
              className="h-8 px-3 bg-transparent border border-white/20 text-white hover:bg-white/10 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5 rotate-180" />
              Reset
            </button>
            {/* Right edge on mobile too, so the dropdown (opens leftward) stays on screen */}
            <div className="ml-auto md:ml-0">
              <FilterPresets page="hcl" current={presetCurrent} onApply={applyPreset} label={columnLabel} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Error / Loading ── */}
      {error && (
        <div className="p-4 m-4 bg-red-900/50 border border-red-500 text-red-200 rounded">
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full">
        <div className="flex-auto overflow-y-auto overflow-x-auto" style={{ willChange: 'transform' }}>
          <table className="w-full text-[12px] tabular-nums min-w-[700px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
              {/* Header Row */}
              <tr className="h-[22px] divide-x divide-white/30 border-b border-white/30">
                <th onClick={() => handleSort('Tanggal')} className={`${thClass('Tanggal')} w-[120px]`}>
                  Tanggal<SortIndicator col="Tanggal" />
                </th>
                <th onClick={() => handleSort('Kode Emiten')} className={`${thClass('Kode Emiten')} w-[80px]`}>
                  Ticker<SortIndicator col="Kode Emiten" />
                </th>
                <th onClick={() => handleSort('Nama Perusahaan')} className={`${thClass('Nama Perusahaan')}`}>
                  Nama Perusahaan<SortIndicator col="Nama Perusahaan" />
                </th>
                <th onClick={() => handleSort('HCL (%)')} className={`${thClass('HCL (%)')} w-[150px]`}>
                  Consentrate<SortIndicator col="HCL (%)" />
                </th>
                <th onClick={() => handleSort('Status')} className={`${thClass('Status')} w-[120px]`}>
                  Status<SortIndicator col="Status" />
                </th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                {['Tanggal', 'Kode Emiten', 'Nama Perusahaan', 'HCL (%)', 'Status'].map(col => (
                  <td key={col} className="px-1 py-0">
                    <DebouncedInput
                      type="text"
                      placeholder="Filter"
                      className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                      value={columnFilters[col] || ''}
                      onChange={val => setColumnFilters(prev => ({ ...prev, [col]: val }))}
                    />
                  </td>
                ))}
              </tr>
            </thead>
            
            <tbody>
              {sortedData.length > 0 ? (
                sortedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-white/[0.25] transition-colors h-[22px] divide-x divide-white/30">
                    <td className="px-2 py-0 text-center text-gray-400">{formatDate(row.Tanggal)}</td>
                    <td className="px-2 py-0 text-center font-medium text-gray-300">{row['Kode Emiten']}</td>
                    <td className="px-2 py-0 text-left text-gray-300 truncate max-w-[240px]">{row['Nama Perusahaan']}</td>
                    <td className="px-2 py-0 text-center font-medium text-pink-400">
                      {row.Status?.toLowerCase() === 'pencabutan' ? '-' : `${row['HCL (%)']}%`}
                    </td>
                    <td className="px-2 py-0 text-center text-gray-400">{row.Status}</td>
                  </tr>
                ))
              ) : !loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    Tidak ada data ditemukan.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
